import { memo, type ReactNode } from 'react';

import { WorkspaceAgentMessageFeedbackCopyButton } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/feedback/WorkspaceAgentMessageFeedbackCopyButton';
import {
  type WorkspaceAgentTableCell,
  type WorkspaceAgentTableColumn,
} from '~/components/v2/components/workspace/workspace-agent-panel/schemas/workspaceAgentTableArtifact';
import {
  type PillMonikerSegment,
  splitPillMonikers,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/splitPillMonikers';

const cellText = (cell: WorkspaceAgentTableCell | undefined): string =>
  cell === null || cell === undefined ? '' : String(cell);

export type WorkspaceAgentTableMonikerSegment = Extract<PillMonikerSegment, { type: 'pill' | 'unknownMoniker' }>;

export type WorkspaceAgentTableCellContentProps = {
  cell: WorkspaceAgentTableCell | undefined;
  column: WorkspaceAgentTableColumn;
  /** Draws a moniker chip. Pass `undefined` to keep monikers as text. */
  renderMoniker: ((segment: WorkspaceAgentTableMonikerSegment) => ReactNode) | undefined;
};

const isMonikerSegment = (segment: PillMonikerSegment): segment is WorkspaceAgentTableMonikerSegment =>
  segment.type === 'pill' || segment.type === 'unknownMoniker';

export const WorkspaceAgentTableCellContent = memo(
  ({ cell, column, renderMoniker }: WorkspaceAgentTableCellContentProps) => {
    const text = cellText(cell);
    const segments = splitPillMonikers(text);
    const monikerSegments = renderMoniker !== undefined ? segments.filter(isMonikerSegment) : [];

    if (renderMoniker !== undefined && monikerSegments.length > 0) {
      return (
        <span className="flex flex-wrap items-center gap-1 break-words">
          {segments.map((segment, index) => {
            if (segment.type === 'text') {
              if (segment.value.length === 0) {
                return null;
              }

              return (
                <span
                  className="break-words"
                  key={`text-${index}`} // NOSONAR -- position in the parsed cell is the identity, and the same moniker can repeat
                >
                  {segment.value}
                </span>
              );
            }

            return (
              <span
                className="min-w-0 max-w-full [&_button]:min-w-0 [&_button]:max-w-full [&_button]:shrink"
                key={`moniker-${index}`} // NOSONAR -- position in the parsed cell is the identity, and the same moniker can repeat
              >
                {renderMoniker(segment)}
              </span>
            );
          })}
        </span>
      );
    }

    if (column.kind === 'url' && text.length > 0) {
      return (
        <span className="flex w-full min-w-0 max-w-full items-center gap-1">
          <span
            className="min-w-0 flex-1 truncate font-mono ads:text-secondary text-gray-11"
            data-testid="WORKSPACE_AGENT_TABLE_CELL_URL"
            title={text}
          >
            {text}
          </span>
          <span className="shrink-0">
            <WorkspaceAgentMessageFeedbackCopyButton copyText={text} />
          </span>
        </span>
      );
    }

    return <span className="block break-words ads:text-primary text-gray-12">{text}</span>;
  },
);

WorkspaceAgentTableCellContent.displayName = 'WorkspaceAgentTableCellContent';
